import { useMemo, useState } from "react";
import { AudioProvider } from "../components/Audio";
import Frame from "../components/Frame";
import { CheckIcon } from "../components/Icons";
import ui from "../components/ui.module.css";
import { submissionsOf } from "../data/responses";
import { resolvePolicy } from "../data/shared/responseEvaluation";
import useLocalSession from "../hooks/useLocalSession";
import Activity, { StepScreen } from "./Activity";

const noop = () => {};

// What the learner may be told about one finished step: only what the
// gateway left in. A missing outcome means feedback is still withheld.
const stepNote = (record) => {
  const submissions = submissionsOf(record);
  const tries = submissions.length;
  const outcome = tries ? submissions[tries - 1].outcome : undefined;
  const parts = [];
  if (outcome === "correct") parts.push("Correct");
  else if (outcome === "incorrect") parts.push("Not quite");
  else if (outcome === "ungraded") parts.push("Your teacher will read this");
  else if (tries) parts.push("Handed in");
  if (tries > 1) parts.push(`${tries} tries`);
  return parts.join(" · ");
};

// "Practise again": the same screens and feedback rules, in memory only. It
// creates no attempt, evidence, review or progress, and never changes the
// original work (Handoff 007 §8).
const Practice = ({ activity, policy, onExit }) => {
  const session = useLocalSession(activity, policy);
  if (session.phase === "finished" || session.phase === "unavailable") {
    return (
      <Frame centered onBack={onExit}>
        <span className={`${ui.bigIcon} ${ui.pop}`}>
          <CheckIcon size={72} />
        </span>
        <h1 className={ui.learnerTitle}>
          {session.phase === "finished" ? "Practice finished" : "This can't be practised here"}
        </h1>
        <p className={ui.subline}>Practice is not saved. Your first work has not changed.</p>
        <div className={ui.toolButtons}>
          <button type="button" className={`${ui.button} ${ui.large} ${ui.secondary}`} onClick={session.restart}>
            Practise again
          </button>
          <button type="button" className={`${ui.button} ${ui.large} ${ui.primary}`} onClick={onExit}>
            Back
          </button>
        </div>
      </Frame>
    );
  }
  return (
    <>
      <aside className={ui.practiceBanner}>Practice · nothing is saved</aside>
      <Activity session={session} onBack={onExit} />
    </>
  );
};

// A finished piece of work, rebuilt from what the learner handed in, through
// the same screens in read-only mode. Nothing here writes anything.
const Revision = ({ attempt, activity, title, onBack }) => {
  const [practising, setPractising] = useState(false);
  const readOnly = useMemo(
    () => ({
      attempt,
      runtime: {},
      verdict: null,
      saving: false,
      completeStep: noop,
      saveProgress: noop,
      unavailable: noop,
    }),
    [attempt]
  );
  const policy = useMemo(() => {
    if (!activity) return null;
    const authored = resolvePolicy(activity.definition, "live") || resolvePolicy({}, "live");
    // Feedback here follows what the gateway already permits: shown only when
    // it sent the answers, never otherwise.
    return {
      ...authored,
      feedbackTiming: activity.answersWithheld ? "after_teacher_review" : "immediate",
    };
  }, [activity]);

  if (!activity) {
    return (
      <Frame centered onBack={onBack}>
        <h1 className={ui.learnerTitle}>{title || "My work"}</h1>
        <p className={ui.subline}>This work can't be shown again here. It is still saved.</p>
      </Frame>
    );
  }
  if (practising) {
    return <Practice activity={activity} policy={policy} onExit={() => setPractising(false)} />;
  }
  return (
    <Frame narrow onBack={onBack}>
      <h1 className={ui.learnerTitle}>{activity.title || title || "My work"}</h1>
      <button
        type="button"
        className={`${ui.button} ${ui.large} ${ui.primary}`}
        onClick={() => setPractising(true)}
      >
        Practise again
      </button>
      <AudioProvider>
        {activity.steps.map((step, index) => {
          const record = attempt.responses?.[step.id];
          const note = stepNote(record);
          return (
            <section key={step.id} className={ui.reviewStep} aria-label={`Part ${index + 1}`}>
              <fieldset disabled style={{ border: 0, width: "100%" }}>
                <legend className={ui.visuallyHidden}>Part {index + 1}, as you did it</legend>
                <StepScreen session={readOnly} step={step} readOnly />
              </fieldset>
              {note && <p className={ui.notice}>{note}</p>}
            </section>
          );
        })}
      </AudioProvider>
    </Frame>
  );
};

export default Revision;
